import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as PR from "../../../prime-react/index";
import moment from "moment";
import { getAlertsCount } from "../../../services/api";
import { areDateRangesEqual, defaultPlayerDateRange, exportFile, formatResponseTypeLabel, graphOptionsMap, memberAnalystChartTitles, optionsforResponseType } from "../../../utils/reuse";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./MyAnalytics.scss";
import { jsPDF } from "jspdf";
import { download, generateCsv, mkConfig } from "export-to-csv";
import { useToast } from "../../../context/ToastContext";
import html2canvas from "html2canvas";

const MyAnalyticsGraph = ({ graphType, graphColor, secondGraphColor, isPlayer, optionsData, dates, placeholder, responseKey, graphCountKey, secondGraphCountKey, graphNameKey, isDetailScreen, exportVisible, filteredValues, setExportModalHandler, setPlayerDateHandler, getResponseHandler, locations = [], selectedLocationsData, selectedCustomerLocationsData, selectedCustomerStatesData }) => {
    const [selectedValues, setSelectedValues] = useState(filteredValues?.length ? filteredValues : []);
    const [singleSelectedValue, setSingleSelectedValue] = useState(filteredValues?.length ? filteredValues[0] : null);
    const { showToast } = useToast();

    const [chartData, setChartData] = useState([]);
    const [responseData, setResponseData] = useState([]);
    const menuRight = useRef(null);
    const chartRef = useRef(null);
    const [chartOptions, setChartOptions] = useState(() => {
            const typeConfig = graphOptionsMap[graphType] || {};
            return isDetailScreen ? typeConfig.detail : typeConfig.normal;
    });
    const [defaultDataForResponseType] = useState([
        { label: 'suspicious', color: '#FFCD02', value: 0 },
        { label: 'notsuspicious', color: '#000000', value: 0 },
        { label: 'notoffered', color: '#B38A00', value: 0 }
    ]); const sessionData = useSelector(state => state.auth.sessionData);
    const isExport = useSelector(state => state.auth?.exportPermission);
    const [visible, setVisible] = useState(false);
    const [exportType, setExportType] = useState(null);

    const [selectedLocations, setSelectedLocations] = useState(selectedLocationsData || []);
    const [selectedCustomerLocations, setSelectedCustomerLocations] = useState(selectedCustomerLocationsData || []);
    const [selectedCustomerStates, setSelectedCustomerStates] = useState(selectedCustomerStatesData || []);

    const customerStatesOptions = useMemo(() => {
        if (!selectedCustomerLocations || selectedCustomerLocations.length === 0 || !locations) {
            return [];
        }
        const locationIds = selectedCustomerLocations.map(loc => loc.id);
        const states = locations
            .filter(loc => locationIds.includes(loc.id) && loc.states)
            .flatMap(loc => loc.states)
            .map(stateName => ({ id: stateName, name: stateName }));
        const uniqueStates = [];
        const seen = new Set();
        for (const s of states) {
            if (!seen.has(s.id)) {
                seen.add(s.id);
                uniqueStates.push(s);
            }
        }
        return uniqueStates.sort((a, b) => a.name.localeCompare(b.name));
    }, [selectedCustomerLocations, locations]);

    useEffect(() => {
        if (selectedCustomerStates?.length > 0) {
            const validStateIds = new Set(customerStatesOptions.map(s => s.id));
            const updatedStates = selectedCustomerStates.filter(s => validStateIds.has(s.id));
            if (updatedStates.length !== selectedCustomerStates.length) {
                setSelectedCustomerStates(updatedStates);
            }
        }
    }, [customerStatesOptions, selectedCustomerStates]);

    const headers = useMemo(() => {
        return { sessionid: sessionData.sessionId };
    }, [sessionData.sessionId]);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const goToAnalyticsDetails = useCallback((type) => {
        navigate("/my-analytics-details?type=" + type, { state: { graphType, graphColor, secondGraphColor, optionsData, selectedDates: dates, placeholder, responseKey, graphCountKey, secondGraphCountKey, graphNameKey, selectedValuesData: selectedValues, selectedLocationsData: selectedLocations, selectedCustomerLocationsData: selectedCustomerLocations, selectedCustomerStatesData: selectedCustomerStates, locations } });
    }, [navigate, graphType, graphColor, secondGraphColor, optionsData, dates, placeholder, responseKey, graphCountKey, secondGraphCountKey, graphNameKey, selectedValues, selectedLocations, selectedCustomerLocations, selectedCustomerStates, locations]);

    const items = useMemo(() => [
        {
            label: 'View',
            icon: 'pi pi-eye',
            disabled: !responseData?.length,
            command: () => goToAnalyticsDetails(graphType)
        },
        {
            label: 'Export',
            icon: 'pi pi-upload',
            disabled: !isExport || !responseData?.length,
            command: () => setVisible(true)
        }
    ], [responseData, isExport, goToAnalyticsDetails, graphType]);

    const processData = useCallback((data, setDataFunction) => {
        if (data) {
            if (graphType === 'rtps') {
                const updatedData = data.map((item, index) => ({
                    label: item?.responseType || '',
                    color: defaultDataForResponseType[index]?.color || '#000000',
                    value: item?.responseRate || 0
                })) || defaultDataForResponseType;;

                const chartData = {
                    labels: updatedData.map(() => 'Response Type'),
                    datasets: updatedData.map(item => ({
                        label: item.label,
                        data: [item.value],
                        backgroundColor: item.color,
                        borderRadius: 5,
                        barThickness: 30,
                        borderWidth: 0,
                        borderColor: "#fff"
                    }))
                };
                setDataFunction(chartData);
            } else {
                let filteredData = data?.filter(item => {
                    const value = item?.[graphCountKey] || 0;
                    return value > 0;
                });
                if (graphType === 'pcps' || graphType === 'mcps') {
                    const sports = filteredData.map(f => f.sportName);
                    const uniqueSports = [...new Set(sports)];
                    let colors =  [ '#FFCD02', '#E6B802', '#CCA302', '#B38E02', '#997902', '#806402', '#665002', '#4D3B01', '#999999', '#808080', '#666', '#4D4D4D', '#333333', '#252525', '#000000' ];
                    const sportColorMap = uniqueSports.reduce((map, sport, index) => {
                        map[sport] = colors[index % colors.length];
                        return map;
                    }, {});
                    filteredData = filteredData.map(item => ({
                        ...item,
                        color: sportColorMap[item.sportName]
                    })).sort((a, b) => {
                        const sportA = a?.sportName?.toLowerCase() || '';
                        const sportB = b?.sportName?.toLowerCase() || '';
                        return sportA.localeCompare(sportB);
                    });
                }
                const labels = filteredData?.map(item => (graphType === 'pcps' || graphType === 'mcps') ? `${item?.[graphNameKey]} (${item?.['sportName']})` : `${item?.[graphNameKey]}`);
                const dataValues = filteredData?.map(item => item?.[graphCountKey] || 0);
                let firstGraphData = {
                    data: dataValues,
                    backgroundColor: (graphType === 'pcps' || graphType === 'mcps') ? filteredData.map(f => f.color) : (graphColor ? graphColor : '#3067C2'),
                    borderRadius: 5,
                    barThickness: 25,
                }
                let datasets = [firstGraphData]
                if (secondGraphCountKey) {
                    const secoundDataValues = filteredData?.map(item => item?.[secondGraphCountKey] || null);
                    const secondGrapData = {
                        label: 'Suspicious Alerts',
                        data: secoundDataValues,
                        backgroundColor: (graphType === 'pcps' || graphType === 'mcps') ? filteredData.map(f => f.color) : (secondGraphColor ? secondGraphColor : '#3067C2'),
                        borderRadius: 5,
                        barThickness: 25,
                    }
                    firstGraphData.label = 'Total Alerts'
                    datasets = [firstGraphData, secondGrapData]
                }
                const chartData = {
                    labels: labels,
                    datasets
                };
                setDataFunction(chartData);
                const typeConfig = graphOptionsMap[graphType] || {};
                setChartOptions(isDetailScreen ? typeConfig.detail : typeConfig.normal);
            }

        } else {
            setDataFunction({
                labels: null,
                datasets: [
                    {
                        label: 'Members',
                        data: null,
                        backgroundColor: graphColor ? graphColor : '#3067C2',
                        borderRadius: 5,
                        barThickness: 25,
                    },
                ]
            });
        }
    }, [defaultDataForResponseType, graphColor, secondGraphColor, graphCountKey, secondGraphCountKey, graphNameKey, graphType, isDetailScreen]);

    const formatDates = (dates) => {
        if (!dates || dates.length === 0) return {};
        const startDate = moment(dates[0]).format('YYYY-MM-DD');
        const endDate = dates[1] ? moment(dates[1]).format('YYYY-MM-DD') : startDate;
        return { startDate, endDate };
    };

    const fetchData = useCallback((type, selectedOptions, setState, setChart, selectedLocs = [], selectedCustLocs = [], selectedCustStates = []) => {
        const dateData = formatDates(dates);
        const obj = {
            type,
            ...dateData,
        };
        if (selectedOptions?.length > 0) {
            if (type === 'tapl') {
                obj.locations = selectedOptions.map(p => p.id).join(',')
            } else {
                obj.sport = selectedOptions.map(p => p.id).join(',')
            }
        }
        let body = {};
        if (type === 'mcps') {
            body = {
                eventLocations: selectedLocs?.length > 0 ? selectedLocs.map(l => l.id) : [],
                customerLocations: selectedCustLocs?.length > 0 ? selectedCustLocs.map(d => {
                    const stateList = selectedCustStates?.map(s => s.id) || [];
                    return d.states?.length ? { name: d.name, states: d.states.filter(id => stateList.includes(id)) } : { name: d.name };
                }) : []
            };
        }

        getAlertsCount(obj, headers, dispatch, (response) => {
            if (response.result === 'SUCCESS') {
                const apiResponse = response.data;
                let responseData = apiResponse?.[responseKey] || [];
                if(graphType === 'rrps') {
                    responseData = responseData.filter(item => item?.[graphCountKey] > 0);
                }
                setState(responseData);
                processData(responseData, setChart);
                if(getResponseHandler) {        
                  getResponseHandler(responseData);
                }
            }
        }, body);
    }, [dates, headers, dispatch, graphCountKey, responseKey, graphType, processData, getResponseHandler]);
    const getAPIHandler = useCallback((selectedGroups, selectedLocs, selectedCustLocs, selectedCustStates) => {
        fetchData(graphType, selectedGroups, setResponseData, setChartData, selectedLocs, selectedCustLocs, selectedCustStates);
    }, [fetchData, graphType]);

    useEffect(() => {
        if (isDetailScreen) {
            setVisible(exportVisible);
        }
    }, [exportVisible, isDetailScreen])

    useEffect(() => {
        if (!dates.length) return;
        let selectedData = [];
        if (!isDetailScreen || isPlayer) {
            if (graphType === 'rtps') {
                selectedData = singleSelectedValue ? [singleSelectedValue] : [];
            } else {
                selectedData = selectedValues;
            }
        } else {
            if (graphType === 'rtps') {
                selectedData = filteredValues?.length ? filteredValues : [];
            } else {
                selectedData = filteredValues;
            }
        }
        const activeLocations = (!isDetailScreen || isPlayer) ? selectedLocations : selectedLocationsData;
        const activeCustomerLocations = (!isDetailScreen || isPlayer) ? selectedCustomerLocations : selectedCustomerLocationsData;
        const activeCustomerStates = (!isDetailScreen || isPlayer) ? selectedCustomerStates : selectedCustomerStatesData;
        getAPIHandler(selectedData, activeLocations, activeCustomerLocations, activeCustomerStates);
    }, [getAPIHandler, selectedValues, dates, graphType, singleSelectedValue, isDetailScreen, filteredValues, isPlayer, selectedLocations, selectedCustomerLocations, selectedCustomerStates, selectedLocationsData, selectedCustomerLocationsData, selectedCustomerStatesData]);

    const getDateForFileName = () => {
        let currDate = `_${moment(dates[0]).format('DD_MMM_YYYY')}${moment(dates[1]).isValid() ? `_to_${moment(dates[1]).format('DD_MMM_YYYY')}` : ''}`;
        if (graphType === 'pcps' || graphType === 'mcps') {
            const startDate = moment().subtract(2, 'years').startOf('day');
            const endDate = moment().endOf('day');
            const formattedStart = startDate.format('DD_MMM_YYYY');
            const formattedEnd = endDate.format('DD_MMM_YYYY');
            currDate = `_${formattedStart}_to_${formattedEnd}`;
        }
        return currDate;
    }
    const downloadHandler = (exportType) => {
        const currDate = getDateForFileName();
        const downloadedFilename = memberAnalystChartTitles[graphType]?.fileName + currDate;
        const csvConfig = mkConfig({ useKeysAsHeaders: true, filename: downloadedFilename });

        if (exportType === 'png') {
            if (graphType === 'rtps') {
                exportFile("rtpsFileDownload", "png", downloadedFilename);
            } else
                if (chartRef.current) {
                    const base64Image = chartRef.current.getBase64Image();
                    const link = document.createElement('a');
                    link.href = base64Image;
                    link.download = `${downloadedFilename}`;
                    link.click();
                }
        }
        else if (exportType === 'csv') {
            const csv = generateCsv(csvConfig)(responseData);
            download(csvConfig)(csv)
        }

        else if (exportType === 'pdf' && !isDetailScreen) {
            if (graphType === 'rtps') {
                exportFile("rtpsFileDownload", "pdf", downloadedFilename);
            } else if (chartRef.current) {
                const base64Image = chartRef.current.getBase64Image();
                const doc = new jsPDF({
                    orientation: "landscape",
                    unit: "mm",
                    format: "a4",
                });

                //  A4 landscape dimensions
                // const pageWidth = doc.internal.pageSize.getWidth();
                // const pageHeight = doc.internal.pageSize.getHeight();

                // // Optional margins
                // const margin = 10;
                // const imageWidth = pageWidth - margin * 2;
                // const imageHeight = pageHeight - margin * 2;

                // doc.addImage(base64Image, 'PNG', margin, margin, imageWidth, imageHeight);
                doc.addImage(base64Image, 'PNG', 10, 10);

                doc.save(`${downloadedFilename}.pdf`);
            }
        }
        else if (exportType === 'pdf' && isDetailScreen) {
            if (graphType === 'rtps') {
                exportFile("rtpsFileDownload", "pdf", downloadedFilename);
            } else {
                const chartElement = document.getElementById(`chartContainer-${graphType}`); // Use the container div directly
                if (chartElement) {
                    html2canvas(chartElement, { scale: 3 }).then((canvas) => { // Increase scale for higher resolution
                        const imgData = canvas.toDataURL('image/png');
                        const pdf = new jsPDF({
                            orientation: "landscape",
                            unit: "mm",
                            format: "a4",
                        });
                        const pdfWidth = pdf.internal.pageSize.getWidth();
                        const pdfHeight = pdf.internal.pageSize.getHeight();
                        pdf.addImage(imgData, 'PNG', 10, 10, pdfWidth - 20, pdfHeight - 20);
                        pdf.save(`${downloadedFilename}.pdf`);
                    }).catch((error) => {
                        console.error("Error capturing chart with html2canvas:", error);
                    });
                } else {
                    console.error("Chart container not found!");
                }
            }

        }
        setExportType(null);
        setVisibleHandler(false)

    }
    const setVisibleHandler = (value) => {
        if (isDetailScreen && !isPlayer) {
            setExportModalHandler(value)
        } else {
            setVisible(value);
        }
    }
    const footerContent = (
        <div>
            <PR.Button label="Cancel" onClick={() => setVisibleHandler(false)} outlined className="logout-btns" />
            <PR.Button label="Export" onClick={() => downloadHandler(exportType)} disabled={!exportType} outlined autoFocus className={`logout-btns click-btn`} />
        </div>
    );
    const responseGraphDetials = () => {
        return <>
            <div ref={chartRef} id="rtpsFileDownload">
                <div onClick={() => goToAnalyticsDetails(graphType)} className={`${!responseData ? 'inactive-link' : 'active-link'} card`} style={responseData?.length > 25 ? { overflow: "auto", height: '400px' } : {}}>
                    <PR.Chart type="bar" data={chartData} options={optionsforResponseType} className='horizontal-bar-chart' />
                </div>
                {responseData?.length ? (
                    <div className="chart-info">
                        {responseData.slice(0, 3).map((item, index) => {
                            const indicatorClass =
                                index === 0
                                    ? 'indicator'
                                    : index === 1
                                        ? 'indicator not-suspicious'
                                        : 'indicator not-offered';

                            return (
                                <ul key={index} className="flex align-items-center justify-content-between list-none ">
                                    <li>
                                        <span className={indicatorClass}></span>
                                        {formatResponseTypeLabel[item?.responseType] || ''}
                                    </li>
                                    <li>
                                        <b>{item?.responseRate?.toFixed(2) ?? 0}%</b>
                                    </li>
                                </ul>
                            );
                        })}
                    </div>
                ) : (
                    <div className="flex justify-content-center">
                        <p className="mt-3">No data available</p>
                    </div>
                )}
            </div>
        </>

    }
    const responseTypeGraph = () => {
        return <>
            <div className="info-box">
                <div className="flex align-items-center header justify-content-between">
                    <div className='pr-2'>
                        <span>{memberAnalystChartTitles[graphType]?.heading}</span>
                    </div>
                    <div className="card flex gap-2 justify-content-center">
                        <div className="card">
                            <PR.Dropdown
                                value={singleSelectedValue}
                                onChange={(e) => setSingleSelectedValue(e.value)}
                                options={optionsData}
                                optionLabel="name"
                                placeholder="Select sports"
                                className="single-select-dropdown"
                                showClear
                                filter
                            />
                        </div>
                        <PR.Menu model={items} popup ref={menuRight} id="popup_menu_right" className="export-popup" popupAlignment="right" />
                        <PR.Button icon="pi pi-ellipsis-v" className="export-button" onClick={(event) => menuRight.current.toggle(event)} aria-controls="popup_menu_right" aria-haspopup />
                    </div>
                </div>
                <div className='pr-0'>
                    <h4>{memberAnalystChartTitles[graphType]?.title}</h4>
                    <p>{memberAnalystChartTitles[graphType]?.description}</p>
                </div>
                {responseGraphDetials()}
            </div>
        </>
    }
    const isDefaultPlayerDateRange = areDateRangesEqual(dates, defaultPlayerDateRange);

    const athletesTemplate = () => (
        <section className="my-analytics-tab player-graph">
            <div className="flex align-items-center justify-content-between gap-3">
                <div className='heading-card'>
                    <h1>{memberAnalystChartTitles[graphType]?.title}</h1>
                    <p>{memberAnalystChartTitles[graphType]?.description}</p>
                </div>
                <div className="header-right flex gap-2 flex-wrap">
                    <div className='date custom-clear-calendar'>
                        <PR.Calendar
                            value={dates}
                            onChange={(e) => setPlayerDateHandler(e.value)}
                            selectionMode="range"
                            iconPos="left"
                            readOnlyInput
                            showIcon
                            className='date-calendar'
                            placeholder='Date'
                            dateFormat="dd/mm/yy"
                            maxDate={new Date()}
                        />
                        {!isDefaultPlayerDateRange &&
                            <div className='custom-clear'>
                                <i className="pi pi-times text-sm" onClick={() => setPlayerDateHandler('remove')}></i>
                            </div>
                        }
                    </div>
                    {placeholder &&
                        <div className="flex flex-row gap-3">
                            <PR.MultiSelect
                                filter
                                value={selectedValues}
                                showSelectAll={optionsData?.length <= 15}
                                onChange={(e) => {
                                    const newValue = e.value || [];
 
                                    if (newValue.length > 15) {
                                        showToast('warn', 'You can select a maximum of 15 items');
                                        return;
                                    }
 
                                    setSelectedValues(newValue);
                                }}
                                options={optionsData}
                                optionLabel="name"
                                placeholder={placeholder}
                                maxSelectedLabels={0}
                                className="multi-select-dropdown"
                                resetFilterOnHide
                                showClear
                                pt={{
                                    checkboxContainer: {
                                        onClick: (e) => {
                                            e.stopPropagation();
                                            const parent = e.target?.parentNode;
                                            if (parent && typeof parent.click === 'function') {
                                                parent.click();
                                            }
                                        },
                                    },
                                }}
                            />
                        </div>
                    }
                    {graphType === 'mcps' && (
                        <>
                            <div className="flex flex-row gap-3">
                                <PR.MultiSelect
                                    filter
                                    value={selectedLocations}
                                    showSelectAll={locations?.length <= 15}
                                    onChange={(e) => {
                                        const newValue = e.value || [];
                                        if (newValue.length > 15) {
                                            showToast('warn', 'You can select a maximum of 15 locations');
                                            return;
                                        }
                                        setSelectedLocations(newValue);
                                    }}
                                    options={locations}
                                    optionLabel="name"
                                    placeholder="Select Event Locations"
                                    maxSelectedLabels={0}
                                    className="multi-select-dropdown"
                                    resetFilterOnHide
                                    showClear
                                    pt={{
                                        checkboxContainer: {
                                            onClick: (e) => {
                                                e.stopPropagation();
                                                const parent = e.target?.parentNode;
                                                if (parent && typeof parent.click === 'function') {
                                                    parent.click();
                                                }
                                            },
                                        },
                                    }}
                                />
                            </div>
                            <div className="flex flex-row gap-3">
                                <PR.MultiSelect
                                    filter
                                    value={selectedCustomerLocations}
                                    showSelectAll={locations?.length <= 15}
                                    onChange={(e) => {
                                        const newValue = e.value || [];
                                        if (newValue.length > 15) {
                                            showToast('warn', 'You can select a maximum of 15 customer locations');
                                            return;
                                        }
                                        setSelectedCustomerLocations(newValue);
                                    }}
                                    options={locations}
                                    optionLabel="name"
                                    placeholder="Select Customer Locations"
                                    maxSelectedLabels={0}
                                    className="multi-select-dropdown"
                                    resetFilterOnHide
                                    showClear
                                    pt={{
                                        checkboxContainer: {
                                            onClick: (e) => {
                                                e.stopPropagation();
                                                const parent = e.target?.parentNode;
                                                if (parent && typeof parent.click === 'function') {
                                                    parent.click();
                                                }
                                            },
                                        },
                                    }}
                                />
                            </div>
                            <div className="flex flex-row gap-3">
                                <PR.MultiSelect
                                    filter
                                    value={selectedCustomerStates}
                                    showSelectAll={customerStatesOptions?.length <= 15}
                                    onChange={(e) => {
                                        const newValue = e.value || [];
                                        if (newValue.length > 15) {
                                            showToast('warn', 'You can select a maximum of 15 customer states');
                                            return;
                                        }
                                        setSelectedCustomerStates(newValue);
                                    }}
                                    options={customerStatesOptions}
                                    optionLabel="name"
                                    placeholder="Select Customer States"
                                    maxSelectedLabels={0}
                                    disabled={!customerStatesOptions.length}
                                    className="multi-select-dropdown customer-states-dropdown"
                                    resetFilterOnHide
                                    showClear
                                    pt={{
                                        checkboxContainer: {
                                            onClick: (e) => {
                                                e.stopPropagation();
                                                const parent = e.target?.parentNode;
                                                if (parent && typeof parent.click === 'function') {
                                                    parent.click();
                                                }
                                            },
                                        },
                                    }}
                                />
                            </div>
                        </>
                    )}
                    <div className="export-block">
                        <PR.Button label="Export" disabled={!isExport || !responseData?.length} onClick={() => { setVisible(true) }} icon="pi pi-upload" className="export-button" />
                    </div>
                </div>
            </div>
        </section>
    )

    return (
        <>
            {(!isDetailScreen && !isPlayer) &&
                (graphType === 'rtps' ? responseTypeGraph() :
                    <div className="info-box">
                        <div className="flex align-items-center header justify-content-between">
                            <div className='pr-2'>
                                <span>{memberAnalystChartTitles[graphType]?.heading}</span>
                            </div>
                            <div className="card flex gap-2 justify-content-center">
                                <div className="card">
                                    {placeholder &&
                                        <PR.MultiSelect
                                            filter
                                            value={selectedValues}
                                            showSelectAll={optionsData?.length <= 15}
                                            onChange={(e) => {
                                                const newValue = e.value || [];

                                                if (newValue.length > 15) {
                                                    showToast('warn', 'You can select a maximum of 15 items');
                                                    return;
                                                }

                                                setSelectedValues(newValue);
                                            }}
                                            options={optionsData}
                                            optionLabel="name"
                                            placeholder={placeholder}
                                            maxSelectedLabels={0}
                                            className="multi-select-dropdown"
                                            resetFilterOnHide
                                            showClear
                                            pt={{
                                                checkboxContainer: {
                                                    onClick: (e) => {
                                                        e.stopPropagation();
                                                        const parent = e.target?.parentNode;
                                                        if (parent && typeof parent.click === 'function') {
                                                            parent.click();
                                                        }
                                                    },
                                                },
                                            }}
                                        />
                                    }
                                </div>
                                <PR.Menu model={items} popup ref={menuRight} id="popup_menu_right" className="export-popup" popupAlignment="right" />
                                <PR.Button icon="pi pi-ellipsis-v" className="export-button" onClick={(event) => menuRight.current.toggle(event)} aria-controls="popup_menu_right" aria-haspopup />
                            </div>
                        </div>
                        <div className='pr-0'>
                            <h4>{memberAnalystChartTitles[graphType]?.title}</h4>
                            <p>{memberAnalystChartTitles[graphType]?.description}</p>
                        </div>
                        <div onClick={() => goToAnalyticsDetails(graphType)} className={`${!responseData ? 'inactive-link' : 'active-link'} card mt-4`} style={responseData?.length > 25 ? { overflow: "auto", height: '400px' } : {}}>
                            <PR.Chart type="bar" ref={chartRef} data={chartData} options={chartOptions} />
                        </div>
                    </div>
                )
            }
            {
                isPlayer && athletesTemplate()
            }
            {(isDetailScreen || isPlayer) &&
                <>
                    <div className="analytics-wrapper">
                        <div className="grid align-items-start mt-3">
                            <div className="col-12">
                                <div className={`info-box ${graphType === 'rtps' ? 'xmax-chart' : 'max-chart'}`}>
                                    {graphType === 'rtps' ? responseGraphDetials() :
                                        <div className="chartbar-details-view" style={{ width: '100%', overflow: "auto" }} id={`chartContainer-${graphType}`}>
                                            <div onClick={() => { if (isPlayer) { goToAnalyticsDetails(graphType) } }} className={`${!responseData ? 'inactive-link' : 'active-link'} card mt-4`} style={responseData?.length > 25 ? { overflow: "auto" } : {}}>
                                                <div style={responseData?.length > 40 ? { width: `${responseData.length * 30}px` } : {}}>
                                                    <PR.Chart type="bar" ref={chartRef} data={chartData} options={chartOptions} height={400} />
                                                </div>
                                            </div>
                                        </div>
                                    }
                                </div>

                            </div>
                        </div>
                    </div>
                </>
            }
            <PR.Dialog visible={visible} footer={footerContent} className="export-dialog">
                <div className="export-header-section">
                    <h2>Export Graph as</h2>
                    <p>Select file format</p>
                </div>
                <div className="export-body-section">
                    <PR.Button label="PNG" onClick={() => setExportType('png')} outlined className={`${exportType === 'png' ? 'export-highlight' : ''} export-btns`} />
                    <PR.Button label="CSV" onClick={() => setExportType('csv')} outlined className={`${exportType === 'csv' ? 'export-highlight' : ''} export-btns`} />
                    <PR.Button label="PDF" onClick={() => setExportType('pdf')} outlined className={`${exportType === 'pdf' ? 'export-highlight' : ''} export-btns`} />
                </div>
            </PR.Dialog>
        </>
    );
};

export default MyAnalyticsGraph;
